import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ItdcrOffice } from 'src/app/shared/models/data/getItdcrOffices';
import { BackendService } from 'src/app/shared/services/backend/backend.service';
import { TeiasoftLoaderService } from 'src/app/shared/services/teiasoft-bucket/teiasoft-loader.service';
import { TeiasoftToasterService } from 'src/app/shared/services/teiasoft-bucket/teiasoft-toaster.service';
import { SendItdcrActionType } from 'src/app/shared/models/data/sendItdcrRequest.model';
import { SelectOption } from 'src/app/shared/teiasoft-bucket/teiasoft-select/teiasoft-select.component';

type RecipientMode = 'office' | 'salesperson';

@Component({
  selector: 'app-modal-send-itdcr',
  templateUrl: './modal-send-itdcr.component.html',
  styleUrl: './modal-send-itdcr.component.scss'
})
export class ModalSendItdcrComponent implements OnInit {
  @Input() itdcrNo!: string;
  @Input() isMobile = false;
  @Input() millMode = false;
  @Input() theme: string = 'silver';

  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<void>();

  // UI state
  mode: RecipientMode = 'office';
  notes = '';

  // Office list/state
  sendToOptionList: SelectOption[] = [];
  selectedOfficeId: string = '';
  hasOffices = false;

  constructor(
    private backendService: BackendService,
    private loaderService: TeiasoftLoaderService,
    private toastService: TeiasoftToasterService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadItdcrOffices();
  }

  onModeChange(newMode: RecipientMode) {
    this.mode = newMode;

    // Keep invariant: salesperson => no office, office => salesperson=false
    if (this.mode === 'salesperson') {
      this.selectedOfficeId = '';
    } else if (this.mode === 'office') {
      // If we have offices but nothing selected, default one
      if (this.hasOffices && !this.selectedOfficeId) {
        const def = this.sendToOptionList.find(o => o.isDefault) ?? this.sendToOptionList[0];
        if (def) this.selectedOfficeId = String(def.value);
      }
    }
  }

  onSelectedOffice(opt: SelectOption) {
    this.selectedOfficeId = String(opt.value);
  }

  get canConfirm(): boolean {
    // salesperson: always OK (no pick needed); office: require a selection
    return this.mode === 'salesperson' || !!this.selectedOfficeId;
  }

  onCloseClick() {
    this.onClose.emit();
  }

  save() {
    const officeId = this.mode === 'office' ? Number(this.selectedOfficeId) : null;
    const actionType = this.mode === 'salesperson'
      ? SendItdcrActionType.ToSalesPerson
      : SendItdcrActionType.ToItdcrOffice;

    this.loaderService.start();
    this.backendService
      .sendItdcr(this.itdcrNo, {
        actionType,
        toItdcrOfficeId: officeId,
        toNotes: this.notes
      })
      .subscribe({
        next: () => {
          this.loaderService.stop();
          this.toastService.show('success', this.translate.instant('itdcr.sentSuccess'));
          this.onSave.emit();
        },
        error: () => {
          this.loaderService.stop();
          this.toastService.show('error', this.translate.instant('commons.error'));
        }
      });
  }

  // --- Data ---
  loadItdcrOffices(): void {
    this.backendService.getItdcrOfficesByItdcr(this.itdcrNo)
      .subscribe(res => this.buildItdcrOffices(res.itdcrOffices));
  }

  buildItdcrOffices(offices: ItdcrOffice[]): void {
    this.sendToOptionList = offices.map(o => {
      const sc = new SelectOption();
      sc.isDefault = o.isDefault;
      sc.text = o.officeName;
      sc.value = o.officeId;
      return sc;
    });

    this.hasOffices = this.sendToOptionList.length > 0;

    if (this.hasOffices) {
      const def = this.sendToOptionList.find(opt => opt.isDefault) ?? this.sendToOptionList[0];
      this.selectedOfficeId = String(def.value);
      this.mode = 'office'; // default to office if available
    } else {
      this.selectedOfficeId = '';
      this.mode = 'salesperson'; // fallback if no offices
    }
  }
}
